import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Footer from '../../components/Footer';
import { resolveAvatarThumbUrl, withAvatarFallback } from '../../utils/avatar';
import { API_BASE } from '../../config';
import useOnboardingGuard from '../../hooks/useOnboardingGuard';
import useNotificationSummary from '../../hooks/useNotificationSummary';

const SYMPATHY_DOT_EVENT = 'tinterest:sympathies-dot-visibility';

const GRID_ROWS_PER_PAGE = 4;
const SYMPATHIES_GRID_BREAKPOINTS = {
  mobile: 767.84,
  twoCols: 1119.98,
  threeCols: 1679.98
};

function getSympathiesGridColumns(width) {
  if (width <= SYMPATHIES_GRID_BREAKPOINTS.mobile) return 1;
  if (width <= SYMPATHIES_GRID_BREAKPOINTS.twoCols) return 2;
  if (width <= SYMPATHIES_GRID_BREAKPOINTS.threeCols) return 3;
  return 4;
}

function Sympathies() {
  const navigate = useNavigate();
  const { loading: onboardingLoading } = useOnboardingGuard();
  const [items, setItems] = useState([]);
  const [likeAnimations, setLikeAnimations] = useState({});
  const [removingIds, setRemovingIds] = useState({});
  const [hiddenIds, setHiddenIds] = useState({});
  const [enteringIds, setEnteringIds] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('likes-me');
  const [tabItems, setTabItems] = useState({
    'likes-me': [],
    'liked-by-me': [],
    mutual: []
  });
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [sessionLikeNotificationIdsByUserId, setSessionLikeNotificationIdsByUserId] = useState(() => new Map());
  const [sessionMutualNotificationIdsByUserId, setSessionMutualNotificationIdsByUserId] = useState(() => new Map());
  const [gridColumns, setGridColumns] = useState(() => (
    typeof window === 'undefined' ? 4 : getSympathiesGridColumns(window.innerWidth)
  ));
  const {
    hasLikeNotifications,
    hasMutualNotifications,
    likeNotificationIds,
    likeNotificationIdsByUserId,
    likeNotificationIdsWithoutUser,
    likeNotificationCount,
    mutualNotificationIdsByUserId,
    markNotificationsRead,
    markSympathyNotificationsReadAll,
    reloadNotifications
  } = useNotificationSummary();
  const loadMoreRef = useRef(null);
  const gridRef = useRef(null);
  const cardRectsRef = useRef(new Map());
  const pendingShiftRef = useRef(false);
  const cardNodeMapRef = useRef(new Map());
  const handledLikeNotificationIdsRef = useRef(new Set());
  const handledMutualNotificationIdsRef = useRef(new Set());
  const initiatedMutualUserIdsRef = useRef(new Set());
  const cleanedLegacyLikeNotificationsRef = useRef(new Set());
  const claimedSympathyNotificationsRef = useRef(false);

  const pageSize = gridColumns * GRID_ROWS_PER_PAGE;
  const likesMeItems = activeTab === 'likes-me' ? items : tabItems['likes-me'];
  const mutualItems = activeTab === 'mutual' ? items : tabItems.mutual;
  const likesMeNotificationIdsByUserId = useMemo(() => {
    const mapped = new Map();

    sessionLikeNotificationIdsByUserId.forEach((ids, userId) => {
      mapped.set(userId, [...ids]);
    });

    if (likeNotificationIdsWithoutUser.length === 0) {
      return mapped;
    }

    let fallbackIndex = 0;
    likesMeItems.forEach((item) => {
      if (fallbackIndex >= likeNotificationIdsWithoutUser.length) return;
      const normalizedUserId = String(item.id);
      if (mapped.has(normalizedUserId)) return;
      mapped.set(normalizedUserId, [likeNotificationIdsWithoutUser[fallbackIndex]]);
      fallbackIndex += 1;
    });

    return mapped;
  }, [sessionLikeNotificationIdsByUserId, likeNotificationIdsWithoutUser, likesMeItems]);
  const mutualNotificationUserIds = useMemo(() => (
    new Set(Array.from(sessionMutualNotificationIdsByUserId.keys()))
  ), [sessionMutualNotificationIdsByUserId]);
  const hasLikesMeCardNotifications = useMemo(() => {
    if (likesMeItems.length === 0) return false;
    return likesMeItems.some((item) => likesMeNotificationIdsByUserId.has(String(item.id)));
  }, [likesMeItems, likesMeNotificationIdsByUserId]);
  const hasMutualCardNotifications = useMemo(() => (
    mutualItems.some((item) => (
      mutualNotificationUserIds.has(String(item.id)) &&
      !initiatedMutualUserIdsRef.current.has(String(item.id))
    ))
  ), [mutualItems, mutualNotificationUserIds]);
  const isMobileViewport = gridColumns === 1;
  const shouldShowFooter = !onboardingLoading && (!loading || items.length > 0 || Boolean(error));

  useEffect(() => {
    window.dispatchEvent(new CustomEvent(SYMPATHY_DOT_EVENT, {
      detail: {
        visible: hasLikesMeCardNotifications || hasMutualCardNotifications
      }
    }));
  }, [hasLikesMeCardNotifications, hasMutualCardNotifications]);

  useEffect(() => {
    setSessionLikeNotificationIdsByUserId((prev) => {
      const next = new Map(prev);
      likeNotificationIdsByUserId.forEach((ids, userId) => {
        const merged = Array.from(new Set([...(next.get(userId) || []), ...ids]));
        next.set(userId, merged);
      });
      return next;
    });
  }, [likeNotificationIdsByUserId]);

  useEffect(() => {
    setSessionMutualNotificationIdsByUserId((prev) => {
      const next = new Map(prev);
      mutualNotificationIdsByUserId.forEach((ids, userId) => {
        const merged = Array.from(new Set([...(next.get(userId) || []), ...ids]));
        next.set(userId, merged);
      });
      return next;
    });
  }, [mutualNotificationIdsByUserId]);

  useEffect(() => {
    const hasUnreadSympathies =
      likeNotificationIds.length > 0 ||
      Array.from(mutualNotificationIdsByUserId.values()).some((ids) => ids.length > 0);

    if (!hasUnreadSympathies) {
      claimedSympathyNotificationsRef.current = false;
      return;
    }

    if (claimedSympathyNotificationsRef.current) {
      return;
    }

    claimedSympathyNotificationsRef.current = true;
    markSympathyNotificationsReadAll().catch(() => {
      claimedSympathyNotificationsRef.current = false;
    });
  }, [likeNotificationIds, markSympathyNotificationsReadAll, mutualNotificationIdsByUserId]);

  const mergeUniqueById = (current, incoming) => {
    const seen = new Set(current.map((x) => x.id));
    const merged = [...current];
    for (const item of incoming) {
      if (!seen.has(item.id)) {
        seen.add(item.id);
        merged.push(item);
      }
    }
    return merged;
  };

  const normalizeItems = useCallback((rawItems, tab) => (
    rawItems.map((item) => ({
      ...item,
      likedByMe: tab === 'mutual' ? true : Boolean(item.isLikedByMe || item.likedByMe || item.isMutual)
    }))
  ), []);

  const rememberCardRects = useCallback((excludedIds = new Set()) => {
    const grid = gridRef.current;
    if (!grid) return;

    const rects = new Map();
    grid.querySelectorAll('[data-sympathy-card-id]').forEach((node) => {
      if (excludedIds.has(node.dataset.sympathyCardId)) return;
      rects.set(node.dataset.sympathyCardId, node.getBoundingClientRect());
    });
    cardRectsRef.current = rects;
  }, []);

  const animateHorizontalShift = useCallback(() => {
    const grid = gridRef.current;
    if (!grid || cardRectsRef.current.size === 0) return;

    const previousRects = cardRectsRef.current;
    const animatedNodes = [];

    grid.querySelectorAll('[data-sympathy-card-id]').forEach((node) => {
      const previousRect = previousRects.get(node.dataset.sympathyCardId);
      if (!previousRect) return;

      const nextRect = node.getBoundingClientRect();
      const changedRows = Math.abs(previousRect.top - nextRect.top) > 2;
      const dx = changedRows
        ? Math.max(nextRect.width, nextRect.left - previousRect.left)
        : previousRect.left - nextRect.left;

      if (Math.abs(dx) < 1) return;

      node.style.transition = 'none';
      node.style.transform = `translateX(${dx}px)`;
      node.style.willChange = 'transform';
      animatedNodes.push(node);
    });

    requestAnimationFrame(() => {
      animatedNodes.forEach((node) => {
        node.style.transition = 'transform 320ms ease';
        node.style.transform = '';
      });

      window.setTimeout(() => {
        animatedNodes.forEach((node) => {
          node.style.transition = '';
          node.style.willChange = '';
        });
      }, 340);

      cardRectsRef.current = new Map();
    });
  }, []);

  useLayoutEffect(() => {
    if (!pendingShiftRef.current) return;
    pendingShiftRef.current = false;
    animateHorizontalShift();
  }, [animateHorizontalShift, items]);

  const load = useCallback(async (tab, cursor = null, append = false) => {
    const token = localStorage.getItem('token');
    if (!token) {
      window.location.href = '/log_in';
      return;
    }

    let endpoint = '';

    switch (tab) {
      case 'likes-me':
        endpoint = '/api/Likes';
        break;
      case 'liked-by-me':
        endpoint = '/api/Likes';
        break;
      case 'mutual':
        endpoint = '/api/Likes';
        break;
      default:
        endpoint = '/api/Likes';
    }

    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError('');

      let requestCursor = cursor;
      let pageCursor = null;
      let mappedItems = [];

      do {
        const pageParams = new URLSearchParams({ count: String(pageSize) });
        pageParams.set('variant', tab);
        if (requestCursor) pageParams.set('cursor', requestCursor);

        const resp = await fetch(`${API_BASE}${endpoint}?${pageParams.toString()}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (resp.status === 401) {
          localStorage.removeItem('token');
          window.location.href = '/log_in';
          return;
        }

        if (!resp.ok) throw new Error('fetch failed');

        const data = await resp.json();
        const nextItems = Array.isArray(data) ? data : (data.items || []);
        pageCursor = !Array.isArray(data) ? data.nextCursor : null;
        mappedItems = mappedItems.concat(normalizeItems(nextItems, tab));
        requestCursor = pageCursor;
      } while (mappedItems.length === 0 && pageCursor);

      setItems((prev) => (append ? mergeUniqueById(prev, mappedItems) : mappedItems));
      if (append && mappedItems.length > 0) {
        setEnteringIds((prev) => {
          const next = { ...prev };
          mappedItems.forEach((item) => {
            next[item.id] = true;
          });
          return next;
        });
      }
      setNextCursor(pageCursor || null);
      setHasMore(Boolean(pageCursor));
    } catch (e) {
      setError('Не удалось загрузить данные');
    } finally {
      if (append) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  }, [normalizeItems, pageSize]);

  useEffect(() => {
    setTabItems((prev) => ({
      ...prev,
      [activeTab]: items
    }));
  }, [activeTab, items]);

  useEffect(() => {
    function updateGridColumns() {
      setGridColumns(getSympathiesGridColumns(window.innerWidth));
    }

    updateGridColumns();
    window.addEventListener('resize', updateGridColumns);
    return () => window.removeEventListener('resize', updateGridColumns);
  }, []);

  const loadMore = useCallback(() => {
    if (loading || loadingMore || !hasMore || !nextCursor) return;
    load(activeTab, nextCursor, true);
  }, [activeTab, hasMore, load, loading, loadingMore, nextCursor]);

  useEffect(() => {
    setItems([]);
    setNextCursor(null);
    setHasMore(false);
    setLikeAnimations({});
    setRemovingIds({});
    setHiddenIds({});
    setEnteringIds({});
    load(activeTab, null, false);
  }, [activeTab, load]);

  useEffect(() => {
    const node = loadMoreRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadMore();
        }
      },
      { root: null, rootMargin: '200px 0px', threshold: 0 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [loadMore, items.length]);

  const toggleLike = async (userId) => {
    const token = localStorage.getItem('token');
    if (!token) {
      window.location.href = '/log_in';
      return;
    }

    const currentItem = items.find((item) => item.id === userId);
    if (!currentItem) return;

    let removalTimer = null;

    try {
      const nextLiked = !currentItem.likedByMe;
      const shouldRemoveFromLikesMe = activeTab === 'likes-me' && nextLiked;

      if (!shouldRemoveFromLikesMe) {
        setItems((prev) => prev.map((item) => (
          item.id === userId ? { ...item, likedByMe: nextLiked } : item
        )));
        setLikeAnimations((prev) => ({
          ...prev,
          [userId]: nextLiked ? 'like' : 'unlike'
        }));
      }

      if (shouldRemoveFromLikesMe) {
        rememberCardRects(new Set([String(userId)]));
        setRemovingIds((prev) => ({ ...prev, [userId]: true }));

        removalTimer = window.setTimeout(() => {
          const fillGap = () => {
            if (hasMore && nextCursor && !loadingMore) {
              load(activeTab, nextCursor, true);
            }
          };

          pendingShiftRef.current = true;
          setHiddenIds((prev) => ({ ...prev, [userId]: true }));
          setItems((prev) => prev.filter((item) => item.id !== userId));
          window.setTimeout(fillGap, 340);
        }, 360);
      }

      const resp = await fetch(`${API_BASE}/api/Likes/like/${userId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (resp.status === 401) {
        localStorage.removeItem('token');
        window.location.href = '/log_in';
        return;
      }

      if (!resp.ok) throw new Error('like failed');
      const data = await resp.json();

      if (data && typeof data.liked === 'boolean') {
        if (!shouldRemoveFromLikesMe) {
          setItems((prev) => prev.map((item) => (
            item.id === userId ? { ...item, likedByMe: data.liked } : item
          )));
        }

        if (shouldRemoveFromLikesMe && data.liked && data.isMutual) {
          initiatedMutualUserIdsRef.current.add(String(userId));
          markLikesMeNotificationsForUserRead(userId);
          markMutualNotificationsForUserRead(userId);
          window.setTimeout(() => {
            reloadNotifications();
          }, 350);
        }
      }
    } catch (e) {
      if (removalTimer !== null) {
        window.clearTimeout(removalTimer);
      }

      setRemovingIds((prev) => {
        const next = { ...prev };
        delete next[userId];
        return next;
      });
      setHiddenIds((prev) => {
        const next = { ...prev };
        delete next[userId];
        return next;
      });
      setItems((prev) => prev.map((item) => (
        item.id === userId ? { ...item, likedByMe: currentItem.likedByMe } : item
      )));
      setItems((prev) => (
        prev.some((item) => item.id === userId)
          ? prev
          : [{ ...currentItem }, ...prev]
      ));
      setLikeAnimations((prev) => ({
        ...prev,
        [userId]: currentItem.likedByMe ? 'like' : 'unlike'
      }));
    }
  };

  const getCardHasNotificationDot = (userId) => {
    const normalizedUserId = String(userId);
    if (activeTab === 'likes-me') {
      return likesMeNotificationIdsByUserId.has(normalizedUserId);
    }
    if (activeTab === 'mutual') {
      if (initiatedMutualUserIdsRef.current.has(normalizedUserId)) {
        return false;
      }
      return mutualNotificationUserIds.has(normalizedUserId);
    }
    return false;
  };

  const markLikesMeNotificationsForUserRead = useCallback((userId) => {
    setSessionLikeNotificationIdsByUserId((prev) => {
      const next = new Map(prev);
      next.delete(String(userId));
      return next;
    });
  }, []);

  const markMutualNotificationsForUserRead = useCallback((userId) => {
    setSessionMutualNotificationIdsByUserId((prev) => {
      const next = new Map(prev);
      next.delete(String(userId));
      return next;
    });
  }, []);

  useEffect(() => {
    const aliveIds = new Set(likeNotificationIds.filter(Boolean));
    handledLikeNotificationIdsRef.current.forEach((id) => {
      if (!aliveIds.has(id)) {
        handledLikeNotificationIdsRef.current.delete(id);
      }
    });
    cleanedLegacyLikeNotificationsRef.current.forEach((id) => {
      if (!aliveIds.has(id)) {
        cleanedLegacyLikeNotificationsRef.current.delete(id);
      }
    });
  }, [likeNotificationIds]);

  useEffect(() => {
    const aliveIds = new Set(
      Array.from(mutualNotificationIdsByUserId.values()).flat().filter(Boolean)
    );
    handledMutualNotificationIdsRef.current.forEach((id) => {
      if (!aliveIds.has(id)) {
        handledMutualNotificationIdsRef.current.delete(id);
      }
    });
  }, [mutualNotificationIdsByUserId]);

  useEffect(() => {
    initiatedMutualUserIdsRef.current.forEach((userId) => {
      const notificationIds = mutualNotificationIdsByUserId.get(String(userId)) || [];
      if (notificationIds.length === 0) return;
      markMutualNotificationsForUserRead(userId);
      initiatedMutualUserIdsRef.current.delete(String(userId));
    });
  }, [markMutualNotificationsForUserRead, mutualNotificationIdsByUserId]);

  useEffect(() => {
    const legacyUnreadIds = likeNotificationIdsWithoutUser.filter(
      (id) => !cleanedLegacyLikeNotificationsRef.current.has(id)
    );

    if (loading || legacyUnreadIds.length === 0) {
      return;
    }

    legacyUnreadIds.forEach((id) => cleanedLegacyLikeNotificationsRef.current.add(id));
    markNotificationsRead(legacyUnreadIds).catch(() => {
      legacyUnreadIds.forEach((id) => cleanedLegacyLikeNotificationsRef.current.delete(id));
    });
  }, [likeNotificationIdsWithoutUser, loading, markNotificationsRead]);

  useEffect(() => {
    if (!isMobileViewport) return undefined;
    const visibilityTolerance = 8;

    const checkVisibleCards = () => {
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;

      cardNodeMapRef.current.forEach((node, userId) => {
        if (!node) return;

        const normalizedUserId = String(userId);
        if (activeTab === 'likes-me' && !likesMeNotificationIdsByUserId.has(normalizedUserId)) return;
        if (activeTab === 'mutual' && !mutualNotificationUserIds.has(normalizedUserId)) return;
        if (activeTab !== 'likes-me' && activeTab !== 'mutual') return;

        const rect = node.getBoundingClientRect();
        const isFullyVisible =
          rect.top >= -visibilityTolerance &&
          rect.bottom <= viewportHeight + visibilityTolerance;

        if (!isFullyVisible) return;

        if (activeTab === 'likes-me') {
          markLikesMeNotificationsForUserRead(userId);
          return;
        }

        if (activeTab === 'mutual') {
          markMutualNotificationsForUserRead(userId);
        }
      });
    };

    const scheduleCheck = () => {
      window.requestAnimationFrame(checkVisibleCards);
    };

    scheduleCheck();
    window.addEventListener('scroll', scheduleCheck, { passive: true });
    window.addEventListener('resize', scheduleCheck);

    return () => {
      window.removeEventListener('scroll', scheduleCheck);
      window.removeEventListener('resize', scheduleCheck);
    };
  }, [
    activeTab,
    isMobileViewport,
    items,
    likesMeNotificationIdsByUserId,
    markLikesMeNotificationsForUserRead,
    markMutualNotificationsForUserRead,
    mutualNotificationUserIds
  ]);

  return (
    <div className="sympathies-page bg-[#FFFBF5] min-h-screen flex flex-col font-['TT_Norms_Pro']">

      <main className="sympathies-page__main flex-grow mx-auto">

        <div className="sympathies-page__back">
          <button onClick={() => navigate(-1)} className="sympathies-page__back-button">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="rotate-0">
              <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Назад
          </button>

        </div>

          <div className="sympathies-page__tabs scrollbar-hide">
            <button
              type="button"
              onClick={() => setActiveTab('likes-me')}
              className={`sympathies-page__tab sympathies-page__tab--likes-me ${
                activeTab === 'likes-me'
                  ? 'sympathies-page__tab--active'
                  : ''
              }`}
            >
              <span className="sympathies-page__tab-label">
                <span>Я нравлюсь</span>
                {hasLikesMeCardNotifications && <span className="sympathies-page__tab-dot" aria-hidden="true" />}
              </span>
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('liked-by-me')}
              className={`sympathies-page__tab sympathies-page__tab--liked-by-me ${
                activeTab === 'liked-by-me'
                  ? 'sympathies-page__tab--active'
                  : ''
              }`}
            >
              <span className="sympathies-page__tab-label">
                <span>Мои лайки</span>
              </span>
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('mutual')}
              className={`sympathies-page__tab sympathies-page__tab--mutual ${
                activeTab === 'mutual'
                  ? 'sympathies-page__tab--active'
                  : ''
              }`}
            >
              <span className="sympathies-page__tab-label">
                <span>Взаимные симпатии</span>
                {hasMutualCardNotifications && <span className="sympathies-page__tab-dot" aria-hidden="true" />}
              </span>
            </button>
          </div>

        {loading && items.length === 0 && (
          <div className="sympathies-page__message">Загрузка...</div>
        )}

        {!loading && error && (
          <div className="sympathies-page__message">{error}</div>
        )}

        {!loading && !error && items.length === 0 && (
          <div className="sympathies-page__message">Список пуст</div>
        )}

        {!loading && !error && items.length > 0 && (
          <div ref={gridRef} className="search-page__grid grid">
            {items.map((user) => {
              const fullNameForAlt = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.nickname || 'Без имени';
              const username = user.nickname ? (user.nickname.startsWith('@') ? user.nickname : '@' + user.nickname) : '';
              const detailsParts = [];
              if (user.city) detailsParts.push(user.city);
              if (typeof user.age === 'number') detailsParts.push(`${user.age} лет`);
              if (user.jobTitle) detailsParts.push(user.jobTitle);

              return (
                <div
                  key={user.id}
                  ref={(node) => {
                    if (node) {
                      cardNodeMapRef.current.set(String(user.id), node);
                    } else {
                      cardNodeMapRef.current.delete(String(user.id));
                    }
                  }}
                  onClick={() => navigate(`/user/${user.id}`)}
                  onMouseEnter={() => {
                    if (activeTab === 'likes-me' && !isMobileViewport) {
                      markLikesMeNotificationsForUserRead(user.id);
                    }
                    if (activeTab === 'mutual' && !isMobileViewport) {
                      markMutualNotificationsForUserRead(user.id);
                    }
                  }}
                  data-sympathy-card-id={String(user.id)}
                  className={`sympathies-page__card-motion search-page__card overflow-hidden flex flex-col group cursor-pointer ${removingIds[user.id] ? 'is-removing' : ''} ${hiddenIds[user.id] ? 'is-hidden' : ''} ${enteringIds[user.id] ? 'is-entering' : ''}`}
                  onAnimationEnd={(event) => {
                    if (event.animationName !== 'sympathy-card-enter-right') return;
                    setEnteringIds((prev) => {
                      const next = { ...prev };
                      delete next[user.id];
                      return next;
                    });
                  }}
                >
                  <div className="search-page__photo aspect-square overflow-hidden relative bg-[#EAEAEA]">
                    {getCardHasNotificationDot(user.id) && <span className="sympathies-page__card-dot" aria-hidden="true" />}
                    <img
                      src={resolveAvatarThumbUrl(user.avatarUrl, 512, 'cover')}
                      onError={withAvatarFallback()}
                      alt={fullNameForAlt}
                      loading="lazy"
                      decoding="async"
                      style={{ objectPosition: '50% 22%' }}
                      className="w-full h-full object-cover"
                    />
                  </div>

                  <div className="search-page__card-body flex flex-col flex-grow items-start text-left">
                    <div className="search-page__name w-full break-words">
                        {user.firstName && <div>{user.firstName}</div>}
                        {user.lastName && <div>{user.lastName}</div>}
                        {(!user.firstName && !user.lastName) && (user.nickname || 'Без имени')}
                      </div>

                    {username && (
                      <div className="search-page__username w-full truncate" title={username}>{username}</div>
                    )}

                    <p
                      className="search-page__details flex-grow opacity-90"
                      title={detailsParts.join(', ')}
                    >
                      {detailsParts.join(', ')}
                    </p>

                    <div className="search-page__card-bottom flex items-center justify-between w-full">
                      <div className="search-page__stats">
                        <div className="search-page__stat">
                          <svg className="search-page__stat-icon translate-y-[0.5px]" viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <mask id={`sympathy-friends-mask-${user.id}`} style={{ maskType: 'alpha' }} maskUnits="userSpaceOnUse" x="0" y="-1" width="23" height="24">
                              <rect y="-0.00012207" width="22.7234" height="22.7234" fill="#D9D9D9"/>
                            </mask>
                            <g mask={`url(#sympathy-friends-mask-${user.id})`}>
                              <path d="M1.70312 16.1756C1.70312 15.6877 1.82905 15.2522 2.0809 14.8692C2.33275 14.4862 2.66911 14.1883 3.08996 13.9754C3.98975 13.5353 4.8945 13.196 5.80423 12.9574C6.71411 12.719 7.71402 12.5997 8.80395 12.5997C9.89405 12.5997 10.894 12.719 11.8037 12.9574C12.7136 13.196 13.6184 13.5353 14.5182 13.9754C14.939 14.1883 15.2754 14.4862 15.5272 14.8692C15.7791 15.2522 15.905 15.6877 15.905 16.1756V16.8421C15.905 17.2232 15.7651 17.5582 15.4853 17.8472C15.2056 18.1361 14.866 18.2806 14.4666 18.2806H3.14133C2.74209 18.2806 2.40258 18.1407 2.1228 17.8609C1.84302 17.581 1.70312 17.2414 1.70312 16.8421V16.1756ZM19.5831 18.2806H17.4327C17.5517 18.0633 17.6424 17.832 17.7049 17.5868C17.7674 17.3416 17.7986 17.0934 17.7986 16.8421V16.0591C17.7986 15.4377 17.6465 14.8454 17.3423 14.2822C17.0379 13.7192 16.6061 13.2361 16.047 12.8329C16.6819 12.9276 17.2846 13.0741 17.8552 13.2724C18.4257 13.471 18.9701 13.7055 19.4885 13.9762C19.9776 14.2372 20.3554 14.5448 20.6218 14.8991C20.8882 15.2532 21.0213 15.6399 21.0213 16.0591V16.8421C21.0213 17.2414 20.8814 17.581 20.6017 17.8609C20.3219 18.1407 19.9824 18.2806 19.5831 18.2806ZM8.80395 11.0702C7.89265 11.0702 7.11256 10.7457 6.46368 10.0968C5.8148 9.44781 5.49036 8.66764 5.49036 7.75633C5.49036 6.84503 5.8148 6.06494 6.46368 5.41606C7.11256 4.76702 7.89265 4.4425 8.80395 4.4425C9.71526 4.4425 10.4954 4.76702 11.1445 5.41606C11.7933 6.06494 12.1178 6.84503 12.1178 7.75633C12.1178 8.66764 11.7933 9.44781 11.1445 10.0968C10.4954 10.7457 9.71526 11.0702 8.80395 11.0702ZM16.9792 7.75633C16.9792 8.66764 16.6547 9.44781 16.0059 10.0968C15.357 10.7457 14.5769 11.0702 13.6656 11.0702C13.5587 11.0702 13.4228 11.058 13.2577 11.0337C13.0925 11.0094 12.9566 10.9827 12.8499 10.9537C13.2233 10.5048 13.5102 10.0067 13.7108 9.45964C13.9112 8.91255 14.0114 8.34446 14.0114 7.75539C14.0114 7.16616 13.9091 6.6002 13.7046 6.05752C13.5001 5.515 13.2152 5.01556 12.8499 4.5592C12.9858 4.5106 13.1217 4.47904 13.2577 4.46452C13.3936 4.44984 13.5296 4.4425 13.6656 4.4425C14.5769 4.4425 15.357 4.76702 16.0059 5.41606C16.6547 6.06494 16.9792 6.84503 16.9792 7.75633ZM3.1231 16.8604H14.4848V16.1756C14.4848 15.9779 14.4353 15.8019 14.3364 15.6477C14.2376 15.4936 14.0808 15.3588 13.8658 15.2435C13.0866 14.8417 12.2843 14.5373 11.4588 14.3303C10.6333 14.1234 9.74839 14.0199 8.80395 14.0199C7.85967 14.0199 6.9748 14.1234 6.14934 14.3303C5.32388 14.5373 4.52154 14.8417 3.74231 15.2435C3.52739 15.3588 3.37046 15.4936 3.27151 15.6477C3.17257 15.8019 3.1231 15.9779 3.1231 16.1756V16.8604ZM8.80395 9.64995C9.3247 9.64995 9.77049 9.46453 10.1413 9.0937C10.5122 8.72287 10.6976 8.27708 10.6976 7.75633C10.6976 7.23559 10.5122 6.7898 10.1413 6.41897C9.77049 6.04813 9.3247 5.86272 8.80395 5.86272C8.28321 5.86272 7.83742 6.04813 7.46659 6.41897C7.09575 6.7898 6.91034 7.23559 6.91034 7.75633C6.91034 8.27708 7.09575 8.72287 7.46659 9.0937C7.83742 9.46453 8.28321 9.64995 8.80395 9.64995Z" fill="#FF6B35"/>
                            </g>
                          </svg>
                          <span className="search-page__stat-count">{user.friendsCount || 0}</span>
                        </div>
                        <div className="search-page__stat">
                          <svg className="search-page__stat-icon" viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <mask id={`sympathy-photos-mask-${user.id}`} style={{ maskType: 'alpha' }} maskUnits="userSpaceOnUse" x="0" y="-1" width="23" height="24">
                              <rect y="-0.00012207" width="22.7234" height="22.7234" fill="#D9D9D9"/>
                            </mask>
                            <g mask={`url(#sympathy-photos-mask-${user.id})`}>
                              <path d="M8.49048 15.9876C8.00407 15.9876 7.59236 15.819 7.25536 15.4816C6.91836 15.1443 6.74985 14.7322 6.74985 14.2453V4.01408C6.74985 3.52719 6.91836 3.11507 7.25536 2.77774C7.59236 2.4404 8.00407 2.27173 8.49048 2.27173H18.7115C19.198 2.27173 19.6097 2.4404 19.9467 2.77774C20.2837 3.11507 20.4522 3.52719 20.4522 4.01408V14.2453C20.4522 14.7322 20.2837 15.1443 19.9467 15.4816C19.6097 15.819 19.198 15.9876 18.7115 15.9876H8.49048ZM8.49048 14.5419H18.7115C18.798 14.5419 18.869 14.5141 18.9246 14.4585C18.9801 14.403 19.0079 14.3319 19.0079 14.2453V4.01408C19.0079 3.92749 18.9801 3.85641 18.9246 3.80083C18.869 3.74525 18.798 3.71746 18.7115 3.71746H8.49048C8.40398 3.71746 8.33297 3.74525 8.27744 3.80083C8.22192 3.85641 8.19415 3.92749 8.19415 4.01408V14.2453C8.19415 14.3319 8.22192 14.403 8.27744 14.4585C8.33297 14.5141 8.40398 14.5419 8.49048 14.5419ZM5.47045 20.4359C4.99014 20.4941 4.55893 20.3778 4.17684 20.087C3.79458 19.7961 3.57456 19.4107 3.51679 18.9309L2.28745 8.98523C2.22936 8.50444 2.35188 8.07225 2.65503 7.68865C2.95801 7.30489 3.34957 7.08216 3.82972 7.02048L4.06322 7.01108C4.26799 6.98152 4.45093 7.03686 4.61205 7.1771C4.77317 7.31734 4.85373 7.49661 4.85373 7.71491C4.85373 7.89691 4.79106 8.05619 4.66573 8.19273C4.54056 8.32927 4.38842 8.4124 4.20933 8.44212L4.0093 8.45127C3.92296 8.45754 3.85508 8.49304 3.80565 8.55778C3.75622 8.62267 3.73769 8.69841 3.75004 8.785L4.97601 18.7456C4.98837 18.8322 5.02536 18.9001 5.08698 18.9495C5.14877 18.9989 5.22283 19.0175 5.30917 19.0051L16.6823 17.5984C16.8871 17.5724 17.068 17.6197 17.2251 17.7403C17.3822 17.8608 17.4728 18.0236 17.4969 18.2285C17.5229 18.4337 17.4756 18.6108 17.3551 18.7601C17.2347 18.9095 17.0722 18.9961 16.8674 19.0201L5.47045 20.4359Z" fill="#FF6B35"/>
                            </g>
                          </svg>
                          <span className="search-page__stat-count">{user.photosCount || 1}</span>
                        </div>
                      </div>
                      <button
                        type="button"
                        className={`search-page__like-button bg-transparent border-none cursor-pointer flex items-center ${
                          likeAnimations[user.id] ? `is-${likeAnimations[user.id]}` : ''
                        }`}
                        onClick={(e) => { e.stopPropagation(); toggleLike(user.id); }}
                        onAnimationEnd={() => {
                          setLikeAnimations((prev) => {
                            const next = { ...prev };
                            delete next[user.id];
                            return next;
                          });
                        }}
                      >
                        <svg className="search-page__like-icon" width="23" height="21" viewBox="0 0 23 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path
                            className={`search-page__like-fill ${user.likedByMe ? 'is-filled' : ''}`}
                            d="M10.7627 2.39844C8.05366 -0.274822 3.20222 0.704711 1.47559 3.94141C0.645736 5.49712 0.503029 7.67294 1.9209 10.3584C3.3114 12.9919 6.20149 16.0907 11.3613 19.4346C16.5208 16.0912 19.4113 12.9928 20.8018 10.3594C22.2196 7.67408 22.0759 5.49819 21.2461 3.94238C19.5195 0.705388 14.669 -0.274697 11.96 2.39844L11.3613 2.98828L10.7627 2.39844Z"
                            fill="#FF6B35"
                          />
                          <path
                            d="M10.7627 2.39844C8.05366 -0.274822 3.20222 0.704711 1.47559 3.94141C0.645736 5.49712 0.503029 7.67294 1.9209 10.3584C3.3114 12.9919 6.20149 16.0907 11.3613 19.4346C16.5208 16.0912 19.4113 12.9928 20.8018 10.3594C22.2196 7.67408 22.0759 5.49819 21.2461 3.94238C19.5195 0.705388 14.669 -0.274697 11.96 2.39844L11.3613 2.98828L10.7627 2.39844Z"
                            stroke="#FF6B35"
                            strokeWidth="1.70426"
                            fill="none"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!loading && !error && items.length > 0 && hasMore && (
          <div ref={loadMoreRef} className="sympathies-page__sentinel" />
        )}

        {loadingMore && (
          <div className="sympathies-page__message sympathies-page__message--more">Загрузка...</div>
        )}

        {!loading && !loadingMore && !error && hasMore && (
          <div className="sympathies-page__more">
            <button
              type="button"
              onClick={loadMore}
              className="sympathies-page__more-button"
            >
              Показать еще
            </button>
          </div>
        )}
      </main>

      {shouldShowFooter && <Footer />}
    </div>
  );
}

export default Sympathies;
